// CreditGauge v1.1 (2026-10-08, cc_tasks row 520, T560)
//
// v1.1: a trial account's header pill reads "FREE TRIAL N days left" (N from
// user_credits_v2.trial_ends_at, 1 on the last day, never 0 or negative). Its
// tooltip says what the trial includes (src/lib/trialFacts.ts, never typed) and
// when it ends. Once the trial has ended, the normal credits pill comes back.
//
// v1.0 (2026-08-01)
//
// Was EnrichmentCreditGauge, and it was lying. It read
// user_enrichment_credits and rendered credits_used_this_period /
// monthly_credit_quota — so the topbar said "Credits 10 / 3021" where a
// person reads "I have 10 left". They had 2,500, in a different table, and
// the 3,021 was a number somebody once typed into a SQL editor.
//
// It now reads user_credits_v2, which is the store a paid action is actually
// checked against, and it renders what is LEFT rather than what is spent.
// Monthly and purchased are shown apart because they behave differently:
// purchased credits stay until spent, monthly ones do not survive the reset.

import { useEffect, useState } from "react";
import { openUpgradeMoment } from "@/lib/upgradeMoment";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { isAdmin } from "@/lib/isAdmin";
import { trialDaysLeftCeil, trialIncludes, loadTrialFacts, type TrialFacts } from "@/lib/trialFacts";

interface CreditRow {
  tier: string;
  trial_ends_at: string | null;
  monthly_credit_grant: number;
  monthly_credits_remaining: number;
  purchased_credits: number;
  monthly_reset_at: string;
}

interface RecentOp {
  action: string;
  cost_credits: number;
  performed_at: string;
}

const fmt = (n: number) => n.toLocaleString();

export function CreditGauge({ compact = false }: { compact?: boolean }) {
  const [row, setRow] = useState<CreditRow | null>(null);
  const [unlimited, setUnlimited] = useState(false);
  const [recent, setRecent] = useState<RecentOp[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);
  const [trial, setTrial] = useState<TrialFacts | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Filtered explicitly, not left to RLS. An unfiltered maybeSingle()
        // returns SOME row rather than THE row, and if the policy ever widens
        // it starts showing a stranger's balance without erroring — which is
        // precisely how this component came to render the wrong number before.
        const { data: auth } = await supabase.auth.getUser();
        const uid = auth?.user?.id;
        const { data } = uid
          ? await supabase
              .from("user_credits_v2" as any)
              .select("tier, trial_ends_at, monthly_credit_grant, monthly_credits_remaining, purchased_credits, monthly_reset_at")
              .eq("user_id", uid)
              .maybeSingle()
          : { data: null };
        if (!cancelled) setRow((data as any) || null);

        // T560: what a running trial includes, for the header pill's tooltip.
        if (compact && String((data as any)?.tier ?? "").toLowerCase() === "trial") {
          const f = await loadTrialFacts().catch(() => null);
          if (!cancelled) setTrial(f);
        }

        // A superadmin's spends are recorded but never deducted. Showing them a
        // finite number that never moves is its own small lie.
        if (uid) {
          const admin = await isAdmin();
          if (!cancelled) setUnlimited(admin);
        }

        if (!compact) {
          const { data: log } = await supabase
            .from("credit_transactions" as any)
            .select("action, cost_credits, performed_at")
            .order("performed_at", { ascending: false })
            .limit(10);
          if (!cancelled) setRecent(((log as any) || []) as RecentOp[]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [compact, refreshTick]);

  // Auto-refresh: window focus, visibility change, and the extension's
  // mfx_credit_gauge_refresh ping after a harvest run.
  useEffect(() => {
    const refresh = () => setRefreshTick((n) => n + 1);
    const onVisibility = () => { if (document.visibilityState === "visible") refresh(); };
    const onWindowMessage = (e: MessageEvent) => {
      if ((e as any)?.data?.type === "mfx_credit_gauge_refresh") refresh();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("message", onWindowMessage);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("message", onWindowMessage);
    };
  }, []);

  if (loading) {
    return compact ? null : <div className="text-xs text-muted-foreground py-2">Loading credits…</div>;
  }
  if (!row) {
    return compact ? null : (
      <Card><CardContent className="py-3 text-xs text-muted-foreground">No credit account yet — one is created the first time you run something that costs credits.</CardContent></Card>
    );
  }

  const monthly = Math.max(0, Number(row.monthly_credits_remaining) || 0);
  const purchased = Math.max(0, Number(row.purchased_credits) || 0);
  const total = monthly + purchased;
  const grant = Math.max(1, Number(row.monthly_credit_grant) || 1);
  const monthlyPct = Math.min(100, Math.round((monthly / grant) * 100));
  const resetAt = new Date(row.monthly_reset_at);
  const daysLeft = Math.max(0, Math.ceil((resetAt.getTime() - Date.now()) / 86400_000));
  const low = !unlimited && total <= Math.max(25, Math.round(grant * 0.2));

  const explain = unlimited ? (
    <div className="text-muted-foreground">
      Your account is not charged. What you run is still recorded, so you can see what
      an action would have cost a customer, but nothing is deducted and nothing
      will stop.
    </div>
  ) : (
    <div className="text-muted-foreground">
      <strong className="text-foreground">{fmt(total)}</strong> left to spend right now.
      <br />
      <strong className="text-foreground">{fmt(monthly)}</strong> from this month's
      allowance of {fmt(grant)}. It resets in {daysLeft} {daysLeft === 1 ? "day" : "days"},
      and anything unused then is not carried over.
      <br />
      <strong className="text-foreground">{fmt(purchased)}</strong> bought. Bought credits
      stay until you spend them.
      <br />
      <span className="mt-1 inline-block">
        At zero, anything that costs credits stops and offers you a top-up. Nothing is
        ever charged to you without you clicking.
      </span>
    </div>
  );

  // T560: a running trial leads with the days left. trialDaysLeftCeil is null once it has ended.
  const trialDays = String(row.tier ?? "").toLowerCase() === "trial" ? trialDaysLeftCeil(row.trial_ends_at) : null;
  if (compact && !unlimited && trialDays !== null) {
    const ends = new Date(String(row.trial_ends_at));
    const endsLabel = ends.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
    const includes = trial ? trialIncludes(trial) : "";
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="flex items-center gap-2 text-xs cursor-help"
            data-testid="header-trial-pill"
            aria-label={`Free trial, ${trialDays} ${trialDays === 1 ? "day" : "days"} left`}
          >
            <Badge variant="outline" className="text-[10px] uppercase tracking-wider font-semibold">Free trial</Badge>
            <span className="text-muted-foreground">{trialDays} {trialDays === 1 ? "day" : "days"} left</span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs max-w-[300px]">
          <div className="font-medium mb-1">Your free trial</div>
          <div className="text-muted-foreground">
            {includes ? <>It includes {includes}. </> : null}
            It ends on {endsLabel}, on its own; nothing is charged. You have {fmt(total)} {total === 1 ? "credit" : "credits"} left.
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (compact) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="flex items-center gap-1.5 text-xs cursor-help"
            aria-label={unlimited ? "Credits — your account is not charged" : `${total} credits left`}
          >
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70">Credits</span>
            {unlimited ? (
              <span className="font-mono text-muted-foreground">not charged</span>
            ) : (
              <>
                <span className={`font-mono ${low ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"}`}>
                  {fmt(total)} left
                </span>
                <div className="w-12 h-1.5 rounded-full bg-muted overflow-hidden" aria-hidden>
                  <div
                    className={`h-full ${low ? "bg-amber-500" : "bg-emerald-500"}`}
                    style={{ width: `${monthlyPct}%` }}
                  />
                </div>
              </>
            )}
            <Badge variant="outline" className="text-[10px] uppercase">{row.tier}</Badge>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs max-w-[300px]">
          <div className="font-medium mb-1">Credits left</div>
          {explain}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Card>
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Credits left</div>
            <div className="font-mono text-base mt-0.5">
              {unlimited ? "not charged" : fmt(total)}
              {!unlimited ? (
                <span className="text-muted-foreground ml-2 text-xs font-sans">
                  {fmt(monthly)} monthly · {fmt(purchased)} bought
                </span>
              ) : null}
            </div>
          </div>
          <Badge variant="default">{row.tier}</Badge>
        </div>

        {!unlimited ? (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-full h-2 rounded-full bg-muted overflow-hidden cursor-help">
                  <div
                    className={`h-full ${low ? "bg-amber-500" : "bg-emerald-500"}`}
                    style={{ width: `${monthlyPct}%` }}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent className="text-xs max-w-[280px]">
                How much of this month's allowance is still unspent. Bought credits are not
                shown in the bar because they do not reset.
              </TooltipContent>
            </Tooltip>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <div>{fmt(monthly)} of {fmt(grant)} monthly left</div>
              <div>resets in {daysLeft} {daysLeft === 1 ? "day" : "days"}</div>
            </div>
          </>
        ) : null}

        <div className="text-xs pt-2 border-t border-border">{explain}</div>

        {low ? (
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
            <div className="text-xs text-amber-600 dark:text-amber-400">
              Running low. At zero, credit-costing actions stop until you top up.
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                {/* cc_tasks row 82: checkout for the right pack or plan, in a new tab. */}
                <Button size="sm" variant="default" onClick={() => openUpgradeMoment({ limitType: "credits", attempting: "Top up your credits", need: { out: true, reason: "You are running low on credits." } })}>
                  Top up
                </Button>
              </TooltipTrigger>
              <TooltipContent className="text-xs">Opens a secure checkout in a new tab for the credit pack or plan that fits. This page stays where it is.</TooltipContent>
            </Tooltip>
          </div>
        ) : null}

        {recent.length > 0 ? (
          <div className="pt-2 border-t border-border">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Recent</div>
            <div className="space-y-1">
              {recent.map((op, i) => (
                <div key={i} className="flex items-center justify-between text-xs font-mono text-muted-foreground">
                  <span className="truncate">{String(op.action || "").replace(/[:_]/g, " ")}</span>
                  <span className="shrink-0 ml-2">{Number(op.cost_credits).toFixed(0)} cr</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

// The old name is kept callable so nothing that still imports it breaks; it is
// the same component reading the right table.
export const EnrichmentCreditGauge = CreditGauge;
